import React, { useState } from "react";
import { MoreVertical, Plus } from "lucide-react";
import { toast } from "sonner";
import {
  useDeleteCallingRuleMutation,
  useGetCallingRegionsQuery,
  useGetCallingRulesQuery,
} from "@/store/admin_api";
import type { CallingRule } from "@/model/interface/CallingRule";
import {
  HOLIDAY_RULE_LABELS,
  RECORDED_MESSAGES_LABELS,
  RULE_BASIS_LABELS,
  summarizeWeek,
} from "@/util/callingRules";
import { RECORDING_CONSENT_LABELS } from "@/enum/RecordingConsent";
import { apiErrorMessage } from "@/util/apiError";
import { formatCalendarDate, formatDate } from "@/util/date";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { CallingRuleDialog, CallingRuleDialogMode } from "./CallingRuleDialog";
import { CallingHolidaysSection } from "./CallingHolidaysSection";

function describeEffectiveDates(rule: CallingRule): string {
  if (rule.effectiveFrom && rule.effectiveTo) {
    return `${formatCalendarDate(rule.effectiveFrom)} to ${formatCalendarDate(rule.effectiveTo)}`;
  }
  if (rule.effectiveFrom) {
    return `From ${formatCalendarDate(rule.effectiveFrom)}`;
  }
  if (rule.effectiveTo) {
    return `Until ${formatCalendarDate(rule.effectiveTo)}`;
  }
  return "In force";
}

export default function CallingRulesPage() {
  const { data: rules, isLoading } = useGetCallingRulesQuery();
  const { data: regions = [] } = useGetCallingRegionsQuery();
  const [deleteRule] = useDeleteCallingRuleMutation();
  const [dialogMode, setDialogMode] = useState<CallingRuleDialogMode>("create");
  const [selectedRule, setSelectedRule] = useState<CallingRule | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const openDialog = (mode: CallingRuleDialogMode, rule: CallingRule | null) => {
    setDialogMode(mode);
    setSelectedRule(rule);
    setIsDialogOpen(true);
  };

  const handleDelete = async (rule: CallingRule) => {
    try {
      await deleteRule(rule.id).unwrap();
      toast.success(`${rule.regionName} rule removed`);
    } catch (error) {
      toast.error(apiErrorMessage(error, "Could not remove the rule"));
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-8 text-foreground">
      <div className="space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-2xl font-bold">Calling Rules</h2>
            <p className="text-sm text-muted-foreground">
              Hours, holidays, daily caps and recorded messages by state. Every lead follows the federal rule and its
              state's rule, and the stricter one wins. States without a row use the federal rule.
            </p>
          </div>
          <Button onClick={() => openDialog("create", null)}>
            <Plus className="mr-2 h-4 w-4" />
            Add rule
          </Button>
        </div>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Region</TableHead>
                <TableHead>Hours</TableHead>
                <TableHead>Holidays</TableHead>
                <TableHead>Calls per 24 h</TableHead>
                <TableHead>Recorded messages</TableHead>
                <TableHead>Recording consent</TableHead>
                <TableHead>Dates</TableHead>
                <TableHead>Basis</TableHead>
                <TableHead>Last edited</TableHead>
                <TableHead className="text-right">Options</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!rules?.length && (
                <TableRow>
                  <TableCell colSpan={10} className="py-8 text-center text-muted-foreground">
                    No calling rules yet
                  </TableCell>
                </TableRow>
              )}
              {rules?.map((rule) => (
                <TableRow key={rule.id}>
                  <TableCell className="font-medium">
                    {rule.regionName}
                    {rule.timeZone && <span className="block text-xs text-muted-foreground">{rule.timeZone}</span>}
                  </TableCell>
                  <TableCell className="max-w-xs text-sm">{summarizeWeek(rule.week)}</TableCell>
                  <TableCell className="text-sm">{HOLIDAY_RULE_LABELS[rule.holidayRule]}</TableCell>
                  <TableCell className="text-sm">{rule.maxCallsPerDay ?? "No cap"}</TableCell>
                  <TableCell className="text-sm">{RECORDED_MESSAGES_LABELS[rule.recordedMessages]}</TableCell>
                  <TableCell className="text-sm">{RECORDING_CONSENT_LABELS[rule.recordingConsent]}</TableCell>
                  <TableCell className="text-sm">{describeEffectiveDates(rule)}</TableCell>
                  <TableCell className="max-w-xs text-sm">
                    <Badge variant="secondary">{RULE_BASIS_LABELS[rule.basis]}</Badge>
                    {rule.source && <span className="mt-1 block text-xs text-muted-foreground">{rule.source}</span>}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {rule.updatedByName ?? "Seed"}
                    {rule.updatedAt && <span className="block text-xs">{formatDate(rule.updatedAt)}</span>}
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button size="sm" variant="ghost" title="Options">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openDialog("edit", rule)}>Edit</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => openDialog("schedule", rule)}>Schedule a change</DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem className="text-destructive" onClick={() => void handleDelete(rule)}>
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <CallingHolidaysSection regions={regions} />

      <CallingRuleDialog
        open={isDialogOpen}
        mode={dialogMode}
        rule={selectedRule}
        regions={regions}
        onOpenChange={setIsDialogOpen}
      />
    </div>
  );
}
